// src/features/projects/weekly/weeklyModel.js
//
// Pure helpers for the Projects Weekly Overview lens: track vocabulary,
// status metadata, deterministic ordering, per-user custom order prefs,
// line correction resolution and week labels.

import { T } from "../../../app/tokens.js";
import { weeklyText } from "../../../lib/weeklyRichText.js";
export { assignedTrackCompleteness, weeklyTrackState } from "../../../lib/weeklyTrackCompleteness.js";
export { currentMondayUtc } from "../../../lib/weeklyCalendar.js";

export { PROJECT_TRACKS as TRACKS } from "../../../lib/projectTracks.js";
import { PROJECT_TRACKS as TRACKS } from "../../../lib/projectTracks.js";

export function trackLabel(key) {
  return TRACKS.find((t) => t.key === key)?.label || key;
}

export const TRACK_STATUS_META = Object.freeze({
  on_track: { label: "On track", color: T.green, bg: T.greenSoft },
  watch: { label: "Watch", color: T.ragAmberText, bg: T.utilAmber },
  blocked: { label: "Blocked", color: T.red, bg: T.utilRed },
});

export const OVERALL_STATUS_META = Object.freeze({
  on_track: { label: "On track", color: T.green, bg: T.greenSoft },
  at_risk: { label: "At risk", color: T.ragAmberText, bg: T.utilAmber },
  critical: { label: "Critical", color: T.red, bg: T.utilRed },
});

// Record health is independent of the weekly summary and track statuses.
export function recordHealthMeta(project) {
  return OVERALL_STATUS_META[project?.recordStatus] || { label: "Unknown health", color: T.mid };
}

// Dot accents are deliberately separate from readable weekly-status text.
const HEALTH_DOT_COLORS = Object.freeze({
  on_track: T.greenDeep, at_risk: T.ragAmber, critical: T.ragRed,
});

// Resolve only the selected week's payload, including captured record health
// in archives. Track statuses and other weeks are never owner-status evidence.
export function weeklyHealthMeta(project) {
  const weekly = project?.summary?.statusOverall;
  const hasWeekly = weekly != null && weekly !== "";
  const status = hasWeekly ? weekly : project?.recordStatus;
  if (!Object.hasOwn(HEALTH_DOT_COLORS, status)) {
    return {
      status: null, source: "unavailable", color: null,
      label: hasWeekly
        ? "Health unavailable - selected-week owner status is unsupported."
        : "Health unavailable - no supported weekly owner status or project record health.",
    };
  }
  const source = hasWeekly ? "weekly" : "record";
  const label = OVERALL_STATUS_META[status].label;
  return {
    status, source, color: HEALTH_DOT_COLORS[status],
    label: hasWeekly ? `Weekly owner status: ${label}`
      : `Project record health fallback: ${label} - weekly owner status not set`,
  };
}

export function hasWeeklyWriteCapability(project) {
  const c = project?.capabilities;
  return Boolean(c && (c.canNote || c.canSummary || c.canCorrectTracks?.length || c.canSetTrackStatus?.length || c.canQuietTracks?.length));
}

export const SORT_MODE = Object.freeze({ REGION: "region", AZ: "az", CUSTOM: "custom" });
export const UPDATES_SORT_OPTIONS = Object.freeze([
  { key: SORT_MODE.AZ, label: "A–Z", subtitle: "Alphabetical by project" },
  { key: SORT_MODE.CUSTOM, label: "Custom", subtitle: "Your arranged order" },
]);
export const VIEW_MODE = Object.freeze({ AGENDA: "agenda", GRID: "grid" });

export const REGION_ORDER = ["AMER", "EMEA", "APAC"];
export const UNKNOWN_REGION = "Unknown region";

export function regionKey(region) {
  const r = typeof region === "string" ? region.trim().toUpperCase() : "";
  return r || null;
}

function regionRank(region) {
  const r = regionKey(region);
  const i = REGION_ORDER.indexOf(r);
  if (i >= 0) return [i, ""];
  if (r) return [REGION_ORDER.length, r];
  return [REGION_ORDER.length + 1, ""];
}

const cmp = (a, b) => String(a ?? "").localeCompare(String(b ?? ""), "en", { sensitivity: "base" });
const idCmp = (a, b) => (String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0);

export function compareByRegion(a, b) {
  const [ra, xa] = regionRank(a.region);
  const [rb, xb] = regionRank(b.region);
  if (ra !== rb) return ra - rb;
  return cmp(xa, xb) || cmp(a.city, b.city) || cmp(a.name, b.name) || idCmp(a, b);
}

export function compareByName(a, b) {
  return cmp(a.name, b.name) || idCmp(a, b);
}

/** Reconcile against selected-week membership, not today's inventory. */
export function applyCustomOrder(projects, storedIds) {
  const base = [...projects].sort(compareByName);
  const byId = new Map(base.map((p) => [p.id, p]));
  const out = [];
  const seen = new Set();
  for (const id of Array.isArray(storedIds) ? storedIds : []) {
    if (byId.has(id) && !seen.has(id)) { out.push(byId.get(id)); seen.add(id); }
  }
  for (const p of base) if (!seen.has(p.id)) out.push(p);
  return out;
}

export function orderProjects(projects, mode, storedIds) {
  const list = Array.isArray(projects) ? projects : [];
  if (mode === SORT_MODE.AZ) return [...list].sort(compareByName);
  if (mode === SORT_MODE.CUSTOM) return applyCustomOrder(list, storedIds);
  return [...list].sort(compareByRegion);
}

/**
 * Write a new visible order back into the stored list while preserving
 * stored IDs that are not currently visible (filtered out / not in week).
 */
export function mergeStoredOrder(storedIds, visibleOrderedIds) {
  const stored = Array.isArray(storedIds) ? storedIds.filter((id) => typeof id === "string") : [];
  const visible = new Set(visibleOrderedIds);
  const queue = [...visibleOrderedIds];
  const result = stored.map((id) => (visible.has(id) ? queue.shift() : id));
  result.push(...queue);
  return [...new Set(result)];
}

export function moveId(ids, id, delta) {
  const i = ids.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const next = [...ids];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// ─── Per-user order preferences (versioned localStorage) ──────────────────────

export const ORDER_PREFS_VERSION = 1;
export function orderPrefsKey(userKey) {
  return userKey ? `projects_weekly_order_v${ORDER_PREFS_VERSION}:${userKey}` : null;
}

export function loadOrderPrefs(userKey, storage = safeStorage()) {
  const fallback = { mode: SORT_MODE.AZ, ids: [] };
  const key = orderPrefsKey(userKey);
  if (!key || !storage) return fallback;
  try {
    const raw = JSON.parse(storage.getItem(key) || "null");
    if (!raw) return fallback;
    // Only the explicit mode is local. Legacy browser IDs are not authoritative
    // and must never override the authenticated server preference.
    const mode = raw.v === ORDER_PREFS_VERSION && UPDATES_SORT_OPTIONS.some((option) => option.key === raw.mode)
      ? raw.mode : SORT_MODE.AZ;
    return { mode, ids: [] };
  } catch {
    return fallback;
  }
}

export function saveOrderPrefs(userKey, prefs, storage = safeStorage()) {
  const key = orderPrefsKey(userKey);
  if (!key || !storage) return;
  try {
    storage.setItem(key, JSON.stringify({ v: ORDER_PREFS_VERSION, mode: prefs.mode }));
  } catch { /* storage full or unavailable: preference only */ }
}

function safeStorage() {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}

// ─── Weeks ────────────────────────────────────────────────────────────────────

export function fmtIsoDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function fmtTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
}

export function weekLabel(iso) {
  return iso ? `Week of ${fmtIsoDate(iso)}` : "";
}

/** Current, unfrozen, non-historical week only. */
export function isWritableWeek(overview, currentWeek) {
  if (!overview) return false;
  return !overview.frozen && !overview.historical && !overview.protectedWeek && overview.weekDate === currentWeek;
}

export function weekMode(overview, currentWeek) {
  if (!overview) return "loading";
  if (overview.frozen) return overview.legacyUnavailable ? "legacy" : "frozen";
  if (overview.historical || overview.weekDate !== currentWeek) return "historical";
  return "current";
}

// ─── Lines and corrections ────────────────────────────────────────────────────

export function resolveLine(line, { rich = false } = {}) {
  const corrections = [...(line?.corrections || [])].sort((a, b) => (a.revision ?? 0) - (b.revision ?? 0));
  const latest = corrections[corrections.length - 1] || null;
  const result = {
    original: line?.note || "",
    current: latest ? (latest.action === "strike" ? null : latest.replacement || "") : line?.note || "",
    struck: latest?.action === "strike",
    corrected: Boolean(latest),
    history: corrections,
  };
  return rich ? result : { ...result, original: weeklyText(result.original), current: result.current === null ? null : weeklyText(result.current),
    history: result.history.map(row => ({ ...row, replacement: row.replacement == null ? row.replacement : weeklyText(row.replacement) })) };
}

export function linesForTrack(project, trackKey) {
  return (project?.lines || []).filter((l) => l.trackKey === trackKey);
}

export function statusForTrack(project, trackKey) {
  return (project?.statuses || []).find((s) => s.trackKey === trackKey) || null;
}

/** Presentation only: corrections and strikes do not erase posted evidence. */
export function partitionWeeklyTracks(project) {
  const active = [], quiet = [];
  for (const track of TRACKS) {
    const hasStatus = Boolean(statusForTrack(project, track.key)?.status);
    (hasStatus || linesForTrack(project, track.key).length > 0 ? active : quiet).push(track);
  }
  return { active, quiet };
}

export function milestoneText(milestone) {
  return milestone?.name
    ? `${milestone.name}${milestone.dueDate ? ` · ${fmtIsoDate(milestone.dueDate)}` : ""}`
    : "None scheduled";
}

export function regionLabel(region) {
  return regionKey(region) || UNKNOWN_REGION;
}

export function groupByRegion(ordered) {
  const groups = [];
  for (const p of ordered) {
    const label = regionLabel(p.region);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(p);
    else groups.push({ label, items: [p] });
  }
  return groups;
}
